import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Share2, Copy, Check, Loader2, Link } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SharePlanbloxModal = ({ open, onOpenChange }) => {
  const { getAuthHeaders } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setCopied(false);
    axios.get(`${API}/referrals/stats`, { headers: getAuthHeaders() })
      .then(res => setReferralData(res.data))
      .catch(() => toast.error('Failed to load referral info'))
      .finally(() => setLoading(false));
  }, [open, getAuthHeaders]);

  // Use current domain for the referral link (works in preview and production)
  const shareUrl = referralData
    ? `${window.location.origin}/register?ref=${referralData.referralCode}`
    : '';

  const shareMessage = referralData
    ? `I'm recommending this — it lets you check what's already happening before you plan anything. You get free credits when you join: ${shareUrl}`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = shareMessage;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleCopyLink = async () => {
    if (!referralData?.shareUrl) return;
    try {
      await navigator.clipboard.writeText(referralData.shareUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="share-planblox-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />
            Share PlanBlox
          </DialogTitle>
          <DialogDescription>
            When someone joins through your link, you earn a credit. They also get free credits when they sign up.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Pre-filled message with link included */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Message + Link (ready to paste)</label>
              <Textarea
                value={shareMessage}
                readOnly
                rows={5}
                className="resize-none text-sm bg-muted/50"
                data-testid="share-message-text"
              />
              <Button
                className="w-full"
                onClick={handleCopy}
                data-testid="copy-message-btn"
              >
                {copied ? (
                  <><Check className="h-4 w-4 mr-2" /> Copied!</>
                ) : (
                  <><Copy className="h-4 w-4 mr-2" /> Copy Message & Link</>
                )}
              </Button>
            </div>

            {/* Share via section */}
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground text-center">
                Paste this message anywhere — texts, email, social media
              </p>
            </div>

            {/* Referral stats */}
            {referralData?.stats && (referralData.stats.total > 0 || referralData.stats.creditsEarned > 0) && (
              <div className="flex items-center justify-center gap-4 pt-2 text-xs text-muted-foreground">
                <span>{referralData.stats.total} shared</span>
                <span>{referralData.stats.credited} joined</span>
                <span className="font-medium text-foreground">{referralData.stats.creditsEarned} credits earned</span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SharePlanbloxModal;
